import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'

const ThemeContext = createContext(null)

const STORAGE_KEY = 'verse-theme'
const ACCENT_KEY = 'verse_accent'

export const ACCENT_PRESETS = {
  purple: { accent: '#7C3AED', hover: '#6D28D9', light: '#F5F0FF', gradient: 'linear-gradient(135deg, #7C3AED, #EC4899)' },
  blue:   { accent: '#2563EB', hover: '#1D4ED8', light: '#EFF6FF', gradient: 'linear-gradient(135deg, #2563EB, #0EA5E9)' },
  rose:   { accent: '#E11D48', hover: '#BE123C', light: '#FFF1F2', gradient: 'linear-gradient(135deg, #E11D48, #F97316)' },
  green:  { accent: '#059669', hover: '#047857', light: '#ECFDF5', gradient: 'linear-gradient(135deg, #059669, #0D9488)' },
  orange: { accent: '#EA580C', hover: '#C2410C', light: '#FFF7ED', gradient: 'linear-gradient(135deg, #EA580C, #F59E0B)' },
  teal:   { accent: '#0D9488', hover: '#0F766E', light: '#CCFBF1', gradient: 'linear-gradient(135deg, #0D9488, #2563EB)' },
}

function applyAccent(preset) {
  const colors = ACCENT_PRESETS[preset] ?? ACCENT_PRESETS.purple
  document.documentElement.style.setProperty('--accent', colors.accent)
  document.documentElement.style.setProperty('--accent-hover', colors.hover)
  document.documentElement.style.setProperty('--accent-light', colors.light)
  document.documentElement.style.setProperty('--accent-gradient', colors.gradient)
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return stored
    if (window.matchMedia('(prefers-contrast: more)').matches) return 'high-contrast'
    return 'light'
  })
  const [accent, setAccent] = useState(() => localStorage.getItem(ACCENT_KEY) ?? 'purple')

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  useEffect(() => {
    applyAccent(accent)
  }, [accent])

  // Listen for accent sync events from AuthContext (profile fetch)
  useEffect(() => {
    function onSync(e) { applyAccent(e.detail); setAccent(e.detail) }
    window.addEventListener('verse-accent-sync', onSync)
    return () => window.removeEventListener('verse-accent-sync', onSync)
  }, [])

  const toggle = useCallback(() => {
    setTheme(t => t === 'dark' ? 'light' : 'dark')
  }, [])

  const setAccentTheme = useCallback((preset) => {
    if (!ACCENT_PRESETS[preset]) return
    setAccent(preset)
    localStorage.setItem(ACCENT_KEY, preset)
    applyAccent(preset)
  }, [])

  const value = useMemo(() => ({
    theme, toggle, setTheme, accent, setAccentTheme, applyAccent
  }), [theme, toggle, setTheme, accent, setAccentTheme])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}
